import { get, post, patch } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'
import type { OnboardingStep, OnboardingEmployee, OnboardingProgress } from '@/types/employee-onboarding'

const API_NAME = 'onboarding'

/**
 * Get role-based onboarding step templates
 */
export const getOnboardingSteps = async (role?: string): Promise<OnboardingStep[]> => {
  const queryParams = new URLSearchParams()
  if (role) queryParams.append('role', role)

  const path = `/onboarding/steps${queryParams.toString() ? `?${queryParams}` : ''}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as OnboardingStep[]
}

/**
 * List employees in onboarding
 */
export const getOnboardingEmployees = async (managerId?: string): Promise<OnboardingEmployee[]> => {
  const queryParams = new URLSearchParams()
  if (managerId) queryParams.append('managerId', managerId)

  const path = `/onboarding/employees${queryParams.toString() ? `?${queryParams}` : ''}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as OnboardingEmployee[]
}

/**
 * Get specific employee onboarding details
 */
export const getOnboardingEmployee = async (employeeId: string): Promise<OnboardingEmployee & { steps: OnboardingStep[] }> => {
  const path = `/onboarding/employees/${employeeId}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as OnboardingEmployee & { steps: OnboardingStep[] }
}

/**
 * Initialize onboarding for a new employee
 */
export const initializeOnboarding = async (employee: {
  employeeId: string
  name: string
  email: string
  role: string
  department: string
  managerId?: string
  startDate: string
}): Promise<OnboardingEmployee & { steps: OnboardingStep[] }> => {
  const path = '/onboarding/employees'

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: employee as any,
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as OnboardingEmployee & { steps: OnboardingStep[] }
}

/**
 * Update onboarding step status
 */
export const updateStepStatus = async (
  employeeId: string,
  stepId: string,
  status: 'pending' | 'in_progress' | 'completed'
): Promise<{ employeeId: string; stepId: string; status: string; onboardingStatus: string }> => {
  const path = `/onboarding/employees/${employeeId}/steps/${stepId}/status`

  const restOperation = patch({
    apiName: API_NAME,
    path,
    options: {
      body: { status } as any,
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as { employeeId: string; stepId: string; status: string; onboardingStatus: string }
}

/**
 * Get aggregated onboarding progress
 */
export const getOnboardingProgress = async (managerId?: string): Promise<OnboardingProgress> => {
  const queryParams = new URLSearchParams()
  if (managerId) queryParams.append('managerId', managerId)

  const path = `/onboarding/progress${queryParams.toString() ? `?${queryParams}` : ''}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as OnboardingProgress
}

export const onboardingApi = {
  getOnboardingSteps,
  getOnboardingEmployees,
  getOnboardingEmployee,
  initializeOnboarding,
  updateStepStatus,
  getOnboardingProgress
}
